import React, { useState } from 'react';
import { Settings, Lightbulb, FileText, BarChart3 } from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import { api } from '../../api/client';
import Navbar from '../shared/Navbar';
import WorkshopForesightTab from './WorkshopForesightTab';
import WorkshopWritingTab from './WorkshopWritingTab';
import CrossSectionAnalysisTab from './CrossSectionAnalysisTab';
import ManageWorkshopModal from './ManageWorkshopModal';

const WorkshopDetailView = ({ workshop: initialWorkshop, activeTab, onTabChange, onNavigateBack, onNavigateToSession, onNavigateToWritingSession }) => {
  const { currentUser } = useAppContext();
  const [workshop, setWorkshop] = useState(initialWorkshop);
  const [showManageModal, setShowManageModal] = useState(false);

  const handleLogout = () => { api.logout(); window.location.reload(); };

  // Frontend heuristics for showing the settings button; the backend is the
  // real authority and will 403 anyone who isn't actually authorized.
  // Workshop settings (rename/password/delete) are admin-only. Participant
  // management is also open to facilitators -- since this view only renders
  // for a workshop the current user can already see, any facilitator here
  // is already a member and thus already allowed to manage participants.
  const canManageSettings = currentUser.role === 'admin';
  const canManageParticipants = currentUser.role === 'admin' || currentUser.role === 'facilitator';
  const showManageButton = canManageSettings || canManageParticipants;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar
        title={workshop.name}
        subtitle={workshop.description}
        onLogout={handleLogout}
        showBack
        onBack={onNavigateBack}
      />

      {showManageModal && (
        <ManageWorkshopModal
          workshop={workshop}
          canEditSettings={canManageSettings}
          onClose={() => setShowManageModal(false)}
          onUpdated={(updated) => { setWorkshop({ ...workshop, ...updated }); setShowManageModal(false); }}
          onDeleted={() => { setShowManageModal(false); onNavigateBack(); }}
        />
      )}

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex bg-white rounded-lg shadow-sm border border-gray-200 p-1">
            <button
              onClick={() => onTabChange('foresight')}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'foresight' ? 'bg-[#01006A] text-white' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Lightbulb className="w-4 h-4" />
              Foresight Sessions
            </button>
            <button
              onClick={() => onTabChange('writing')}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'writing' ? 'bg-[#01006A] text-white' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <FileText className="w-4 h-4" />
              Writing Sessions
            </button>
            {(currentUser.role === 'admin' || currentUser.role === 'facilitator') && (
              <button
                onClick={() => onTabChange('cross-section')}
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  activeTab === 'cross-section' ? 'bg-[#01006A] text-white' : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <BarChart3 className="w-4 h-4" />
                Cross-Section Analysis
              </button>
            )}
          </div>

          {showManageButton && (
            <button
              onClick={() => setShowManageModal(true)}
              className="flex items-center gap-2 text-gray-600 hover:text-[#01006A] border border-gray-300 hover:border-[#01006A] px-3 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              <Settings className="w-4 h-4" />
              {canManageSettings ? 'Workshop Settings' : 'Manage Participants'}
            </button>
          )}
        </div>

        {activeTab === 'foresight' && (
          <WorkshopForesightTab workshop={workshop} onNavigateToSession={onNavigateToSession} />
        )}
        {activeTab === 'writing' && (
          <WorkshopWritingTab workshop={workshop} onNavigateToWritingSession={onNavigateToWritingSession} />
        )}
        {activeTab === 'cross-section' && (currentUser.role === 'admin' || currentUser.role === 'facilitator') && (
          <CrossSectionAnalysisTab workshop={workshop} />
        )}
      </div>
    </div>
  );
};

export default WorkshopDetailView;
